import { CSSProperties, FC } from 'react';

import { styled } from '../../theme';
import OptionButton from './OptionButton';
import { ToggleStripProps } from './types';

export const StripRoot = styled('div')(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    borderRadius: theme.borderRadius.base,
    overflow: 'hidden',
    background: theme.color.secondary1,
}));

export const SlidingIndicator = styled('div', {
    shouldForwardProp: (prop) => !['activeIndex', 'count'].includes(prop),
})<{
    activeIndex: number;
    count: number;
}>(({ theme, activeIndex, count }) => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: `${100 / count}%`,
    background: theme.color.primary1,
    borderRadius: theme.borderRadius.base,
    transform: `translateX(${activeIndex * 100}%)`,
    transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
}));

const ToggleStrip: FC<ToggleStripProps> = ({
    value,
    options,
    onChange,
    height = 40,
    buttonStyle,
    size,
    ariaLabel,
    className,
    tooltipOffset,
}) => {
    const activeIndex = Math.max(
        options.findIndex((option) => option.value === value),
        0
    );
    const containerStyle: CSSProperties = { height };

    return (
        <StripRoot role="group" aria-label={ariaLabel} className={className} style={containerStyle}>
            <SlidingIndicator activeIndex={activeIndex} count={options.length} aria-hidden="true" />
            {options.map((option) => (
                <OptionButton
                    key={option.value || 'default'}
                    option={option}
                    isSelected={option.value === value}
                    isDisabled={option.isDisabled}
                    buttonStyle={buttonStyle}
                    size={size}
                    tooltipOffset={tooltipOffset}
                    onSelect={onChange}
                />
            ))}
        </StripRoot>
    );
};

export default ToggleStrip;
